import { useState, useEffect } from 'react';
import axios from 'axios';
import { adminUsersAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash2, UserCog, Wrench, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminSettingsTab() {
  const { user: currentUser } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, admin: null });
  
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: ''
  });

  useEffect(() => {
    loadAdmins();
  }, []);

  const loadAdmins = async () => {
    try {
      setLoading(true);
      const res = await adminUsersAPI.getAll();
      setAdmins(res.data);
    } catch (error) {
      toast.error('Gagal memuat data admin');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error('Nama wajib diisi');
      return;
    }

    if (!editingAdmin && (!formData.email || !formData.password)) {
      toast.error('Email dan Password wajib diisi untuk admin baru');
      return;
    }

    try {
      if (editingAdmin) {
        const updateData = { name: formData.name };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await adminUsersAPI.update(editingAdmin.id, updateData);
        toast.success('Admin berhasil diupdate');
      } else {
        await adminUsersAPI.create(formData);
        toast.success('Admin berhasil ditambahkan');
      }
      
      setDialogOpen(false);
      resetForm();
      loadAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan admin');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.admin) return;
    
    try {
      await adminUsersAPI.delete(deleteDialog.admin.id);
      toast.success('Admin berhasil dihapus');
      setDeleteDialog({ open: false, admin: null });
      loadAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus admin');
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      name: '',
      password: ''
    });
    setEditingAdmin(null);
  };

  const openEditDialog = (admin) => {
    setEditingAdmin(admin);
    setFormData({
      email: admin.email,
      name: admin.name,
      password: ''
    });
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  // Fix TKP Questions function
  const [fixingTkp, setFixingTkp] = useState(false);
  const handleFixTkpQuestions = async () => {
    try {
      setFixingTkp(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/questions/fix-tkp`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.fixed_count > 0) {
        toast.success(`${response.data.fixed_count} soal TKP berhasil diperbaiki!`);
      } else {
        toast.info('Semua soal TKP sudah dalam kondisi baik.');
      }
    } catch (error) {
      toast.error('Gagal memperbaiki soal TKP');
      console.error(error);
    } finally {
      setFixingTkp(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <h1 className="text-2xl font-bold text-slate-900">Pengaturan Admin</h1>
        <Button onClick={openCreateDialog} className="bg-[#4472C4] hover:bg-[#3461B3]">
          <Plus className="w-4 h-4 mr-2" />
          Tambah Admin
        </Button>
      </div>

      {/* Maintenance Tools */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wrench className="w-5 h-5" />
            Tools Maintenance
          </CardTitle>
          <CardDescription>Alat untuk memperbaiki masalah data</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200">
            <div>
              <h4 className="font-medium text-amber-900">Fix Soal TKP/Berbobot</h4>
              <p className="text-sm text-amber-700">
                Memperbaiki soal TKP yang masih menampilkan centang hijau di opsi A. 
                Soal berbobot seharusnya tidak memiliki jawaban benar tunggal.
              </p>
            </div>
            <Button 
              onClick={handleFixTkpQuestions}
              disabled={fixingTkp}
              variant="outline"
              className="shrink-0 border-amber-500 text-amber-700 hover:bg-amber-100"
            >
              {fixingTkp ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Memperbaiki...
                </>
              ) : (
                <>
                  <Wrench className="w-4 h-4 mr-2" />
                  Fix Sekarang
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Admin List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-4 border-[#4472C4] border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="mt-2 text-slate-500">Memuat...</p>
            </div>
          ) : admins.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <UserCog className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Belum ada admin</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#4472C4] text-white">
                  <tr>
                    <th className="px-4 py-3 text-left">No</th>
                    <th className="px-4 py-3 text-left">Nama Admin</th>
                    <th className="px-4 py-3 text-left">Email</th>
                    <th className="px-4 py-3 text-center">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {admins.map((admin, idx) => (
                    <tr key={admin.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">{idx + 1}</td>
                      <td className="px-4 py-3 font-medium">
                        {admin.name}
                        {admin.id === currentUser?.id && (
                          <span className="ml-2 text-xs text-[#4472C4]">(Anda)</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-500">{admin.email}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditDialog(admin)}
                            className="bg-[#4472C4] text-white hover:bg-[#3461B3]"
                          >
                            Ubah
                          </Button>
                          {admin.id !== currentUser?.id && (
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => setDeleteDialog({ open: true, admin })}
                            >
                              Hapus
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingAdmin ? 'Edit Admin' : 'Tambah Admin'}</DialogTitle>
            <DialogDescription>
              {editingAdmin ? 'Update informasi admin' : 'Tambah admin baru ke sistem'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Nama Admin *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Nama lengkap"
                required
              />
            </div>

            {!editingAdmin && (
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@example.com"
                  required
                />
              </div>
            )}

            <div>
              <Label>{editingAdmin ? 'Password Baru (kosongkan jika tidak diubah)' : 'Password *'}</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="********"
                required={!editingAdmin}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Batal
              </Button>
              <Button type="submit" className="bg-[#4472C4] hover:bg-[#3461B3]">
                {editingAdmin ? 'Update' : 'Tambah'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, admin: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Admin?</AlertDialogTitle>
            <AlertDialogDescription>
              Apakah Anda yakin ingin menghapus admin "{deleteDialog.admin?.name}"?
              Tindakan ini tidak dapat dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
